import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useFeature, useLocalize, useViewer, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Button, GraphQLErrorMessage, Jumbo, Paragraph, QuillText } from '@treams/treams-ui';

import { growthPathDataMappingFromUser } from '../GrowthPathConverter';
import CareerDetailHeader from '../components/CareerDetailHeader';
import GrowthPathDetailBody, {
	GrowthPathDetailBodySkeleton,
	GrowthPathEmpty,
} from '../components/GrowthPathDetailBody';
import GrowthPathHeader from '../components/GrowthPathHeader';
import LabelQueryWrapper from '../components/LabelQueryWrapper';
import { useGrowthPathContext } from '../contexts/GrowthPathContext';
import { GrowthPathDetailContainer } from '../pages/GrowthPath';

const MyJobTitleDetail: FC = () => {
	const { t } = useTranslation();
	const viewer = useViewer();
	const { isManager, isAdministrator, isWorkspaceSuperuser } = useViewerRoles();
	const history = useHistory();
	const { localize } = useLocalize();
	const multiRoleConversion = useFeature(graphql.FeatureName.MultiRoleConversion);
	const hasJobCompetenciesExplorer = useFeature(graphql.FeatureName.JobCompetenciesExplorer);

	const { userID, jobTitleID } = useGrowthPathContext();

	const { refetch: userRefetch } = graphql.useUserQuery({
		variables: { id: userID ?? viewer!.id },
		fetchPolicy: 'cache-and-network',
	});

	const {
		data: userCompetenciesData,
		loading: userCompetenciesLoading,
		error: userCompetenciesError,
	} = graphql.useUserCompetenciesQuery({
		variables: { id: userID ?? viewer!.id },
		fetchPolicy: 'cache-and-network',
	});

	const {
		data: careersData,
		loading: careersLoading,
		error: careersError,
	} = graphql.useCareersQuery({
		variables: { isArchived: false, first: 1000 },
		fetchPolicy: 'cache-and-network',
	});

	const loading = careersLoading || userCompetenciesLoading;
	const error = careersError || userCompetenciesError;

	const career = careersData?.careers.edges.find((edge) =>
		edge.node.jobTitles.some((jobTitle) => jobTitle.id === jobTitleID),
	)?.node;

	const jobPosition = userCompetenciesData?.user.jobPositions.find(
		(jobPosition) => jobPosition.jobTitle.id === jobTitleID,
	);

	const allCompetencies =
		userCompetenciesData?.user.jobPositions.flatMap((jobPosition) => jobPosition.competencies) ??
		[];

	const jobCompetencies = jobPosition ? jobPosition.competencies : allCompetencies;

	const hasDescription =
		jobPosition?.jobTitle?.description?.[0]?.text?.ops?.length &&
		jobPosition?.jobTitle?.description?.[0]?.text?.ops?.length > 0;

	const linkedJobTitle = career?.jobTitles.find(
		(jobTitle) => jobPosition?.jobTitle?.id === jobTitle.id,
	);

	const canTerminateJob = isManager || isAdministrator || isWorkspaceSuperuser;

	const [updateJobPosition] = graphql.useUpdateJobPositionMutation();

	const terminateJobPosition = async () => {
		if (!jobPosition) {
			return;
		}

		await updateJobPosition({
			variables: {
				input: {
					id: jobPosition.id,
					jobTitleID: jobPosition.jobTitle.id,
					startDate: jobPosition.startDate,
					userID: userID,
					endDate: new Date(),
					externalReferenceID: jobPosition.externalReferenceID,
					jobLevelID: jobPosition.jobLevel?.id,
				},
			},
		});

		await userRefetch();

		history.push(`/users/${userID}/growth-path/my-job-title/all`);
	};

	const growthPathData = useMemo(() => {
		if (!userCompetenciesData) {
			return undefined;
		}

		return growthPathDataMappingFromUser(userCompetenciesData.user, jobTitleID);
	}, [userCompetenciesData, jobTitleID]);

	if (loading) {
		return <MyJobTitleDetailSkeleton />;
	}
	if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	}

	const defaultPageTitle = !hasJobCompetenciesExplorer
		? t('sidebar.competencies')
		: t('sidebar.growthPath');

	const overview = !jobPosition;
	const title = jobPosition ? localize(jobPosition.jobTitle.title).text : defaultPageTitle;

	return (
		<GrowthPathDetailContainer>
			<GrowthPathHeader
				title={title}
				description={overview ? t('my.jobTitles.detail.description') : undefined}
				editable={overview && !userID}
				actions={
					jobTitleID !== 'all' && multiRoleConversion && canTerminateJob ? (
						<Button
							type="destructive"
							title={t('career.detail.removeRole')}
							onClick={terminateJobPosition}
						/>
					) : undefined
				}
				extra={
					jobTitleID && career ? (
						<CareerDetailHeader career={career} linkedJobTitle={linkedJobTitle} />
					) : undefined
				}
			>
				{jobPosition && hasDescription && jobPosition.jobTitle.description && (
					<Paragraph py={2}>
						<QuillText value={localize(jobPosition.jobTitle.description).text} />
					</Paragraph>
				)}
			</GrowthPathHeader>
			{growthPathData && userCompetenciesData!.user.jobPositions.length > 0 ? (
				<LabelQueryWrapper jobCompetencies={jobCompetencies} data={userCompetenciesData}>
					<GrowthPathDetailBody data={growthPathData} />
				</LabelQueryWrapper>
			) : (
				<GrowthPathEmpty
					title={t('my.jobTitles.detail.empty.title')}
					description={t('my.jobTitles.detail.empty.description')}
				/>
			)}
		</GrowthPathDetailContainer>
	);
};

const MyJobTitleDetailSkeleton = () => {
	return (
		<>
			<Jumbo.Skeleton wide />
			<GrowthPathDetailBodySkeleton />
		</>
	);
};

export default MyJobTitleDetail;
